/**
 * useConsultationHistory Hook
 * State management for consultation history (completed medical records)
 *
 * @version 2025.1.0
 */

import { useState, useCallback, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import {
  MedicalRecord,
  MedicalRecordFilters,
  MedicalRecordStatus,
  MedicalOpinion,
} from '../types/medicalRecord';
import medicalRecordService from '../services/medicalRecordService';
import { getApiErrorMessage } from '../types/common';

// ============================================================================
// INTERFACES
// ============================================================================

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ConsultationHistoryFilters {
  companyId?: number;
  clinicId?: number;
  professionalId?: number;
  examType?: string;
  medicalOpinion?: MedicalOpinion | MedicalOpinion[];
  startDate?: string;
  endDate?: string;
  search?: string;
}

interface UseConsultationHistoryOptions {
  initialFilters?: ConsultationHistoryFilters;
  autoLoad?: boolean;
}

interface UseConsultationHistoryReturn {
  // State
  records: MedicalRecord[];
  loading: boolean;
  filters: ConsultationHistoryFilters;
  pagination: Pagination;

  // Filter actions
  setFilters: (filters: ConsultationHistoryFilters) => void;
  updateFilter: <K extends keyof ConsultationHistoryFilters>(
    key: K,
    value: ConsultationHistoryFilters[K]
  ) => void;
  clearFilters: () => void;

  // Pagination actions
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;

  // Data actions
  loadHistory: () => Promise<void>;
  refresh: () => Promise<void>;

  // PDF generation
  generateAso: (id: number) => Promise<{ pdfPath: string } | null>;
  generateProntuario: (id: number) => Promise<{ pdfPath: string } | null>;
}

// ============================================================================
// DEFAULTS
// ============================================================================

const defaultFilters: ConsultationHistoryFilters = {};

const defaultPagination: Pagination = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
};

// ============================================================================
// HOOK
// ============================================================================

export function useConsultationHistory(
  options: UseConsultationHistoryOptions = {}
): UseConsultationHistoryReturn {
  const { initialFilters = defaultFilters, autoLoad = true } = options;
  const { enqueueSnackbar } = useSnackbar();

  // Records state
  const [records, setRecords] = useState<MedicalRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFiltersState] = useState<ConsultationHistoryFilters>(initialFilters);
  const [pagination, setPagination] = useState<Pagination>(defaultPagination);

  // =========================================================================
  // LOAD DATA
  // =========================================================================

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      // Build filters for API call
      // Note: clinicId and examType are not supported by backend yet
      const apiFilters: MedicalRecordFilters = {
        status: MedicalRecordStatus.COMPLETED, // Only completed consultations
        companyId: filters.companyId,
        professionalId: filters.professionalId,
        medicalOpinion: filters.medicalOpinion,
        startDate: filters.startDate,
        endDate: filters.endDate,
      };

      const result = await medicalRecordService.getMedicalRecords({
        ...apiFilters,
        page: pagination.page,
        limit: pagination.limit,
        sortBy: 'consultationCompletedAt',
        sortOrder: 'DESC',
      });

      setRecords(result.data);
      setPagination((prev) => ({
        ...prev,
        total: result.pagination.total,
        totalPages: result.pagination.totalPages,
      }));
    } catch (error) {
      console.error('Error loading consultation history:', error);
      enqueueSnackbar('Erro ao carregar histórico de consultas', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit, enqueueSnackbar]);

  const refresh = useCallback(async () => {
    await loadHistory();
  }, [loadHistory]);

  // =========================================================================
  // FILTER ACTIONS
  // =========================================================================

  const setFilters = useCallback((newFilters: ConsultationHistoryFilters) => {
    setFiltersState(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const updateFilter = useCallback(
    <K extends keyof ConsultationHistoryFilters>(key: K, value: ConsultationHistoryFilters[K]) => {
      setFiltersState((prev) => ({ ...prev, [key]: value }));
      setPagination((prev) => ({ ...prev, page: 1 }));
    },
    []
  );

  const clearFilters = useCallback(() => {
    setFiltersState(defaultFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // =========================================================================
  // PAGINATION ACTIONS
  // =========================================================================

  const setPage = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
  }, []);

  // =========================================================================
  // PDF GENERATION
  // =========================================================================

  const generateAso = useCallback(
    async (id: number): Promise<{ pdfPath: string } | null> => {
      try {
        const result = await medicalRecordService.generateAsoPdf(id);
        enqueueSnackbar('ASO gerado com sucesso', { variant: 'success' });
        return result;
      } catch (error) {
        console.error('Error generating ASO:', error);
        const errorMessage = getApiErrorMessage(error, 'Erro ao gerar ASO');
        enqueueSnackbar(errorMessage, { variant: 'error' });
        return null;
      }
    },
    [enqueueSnackbar]
  );

  const generateProntuario = useCallback(
    async (id: number): Promise<{ pdfPath: string } | null> => {
      try {
        const result = await medicalRecordService.generateProntuarioPdf(id);
        enqueueSnackbar('Prontuário gerado com sucesso', { variant: 'success' });
        return result;
      } catch (error) {
        console.error('Error generating Prontuário:', error);
        const errorMessage = getApiErrorMessage(error, 'Erro ao gerar Prontuário');
        enqueueSnackbar(errorMessage, { variant: 'error' });
        return null;
      }
    },
    [enqueueSnackbar]
  );

  // =========================================================================
  // EFFECTS
  // =========================================================================

  // Auto-load on mount and filter/pagination change
  useEffect(() => {
    if (autoLoad) {
      loadHistory();
    }
  }, [loadHistory, autoLoad]);

  // =========================================================================
  // RETURN
  // =========================================================================

  return {
    // State
    records,
    loading,
    filters,
    pagination,

    // Filter actions
    setFilters,
    updateFilter,
    clearFilters,

    // Pagination actions
    setPage,
    setLimit,

    // Data actions
    loadHistory,
    refresh,

    // PDF generation
    generateAso,
    generateProntuario,
  };
}

export default useConsultationHistory;
